import React from 'react'
import {FormattedMessage} from 'react-intl'
import {
    AspectRatio,
    Box,
    Divider,
    Grid,
    GridItem,
    GridProps,
    Image,
    Link,
    LinkProps,
    Stack,
    StackProps,
    Text,
    TextProps,
    useMultiStyleConfig,
} from '@chakra-ui/react'
import {Link as RouteLink} from 'react-router-dom'
import {HideBelowViewport, MenuItem, ShowBelowViewport} from '_uiComponents'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {useAnalytics} from '_customHooks'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {searchUrlBuilder} from '_utils'
import SearchProductsGrid from '../products-grid'
import type {StorySuggestion, SuggestionsColumn} from '_types'

interface SearchResultsProps {
    searchValue: string
    searchSuggestion?: string
    productSuggestionIds: Array<string>
    ideas?: SuggestionsColumn
    storiesSuggestions?: Array<StorySuggestion>
    onResultClick: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void
    onProductClick?: () => void
}

/**
 * @name SearchResults
 *
 * @component
 *
 * @description
 * Renders the results of a search query.
 *
 * @remarks
 * The `useProducts` integration is necessary to retrieve products informations like images and URLs.
 * There is a maximum count for shown stories currently set to 2.
 *
 * @param {SearchResultsProps} props - The properties object.
 * @param {string} props.searchValue - Search value string.
 * @param {string} props.searchSuggestion - The name for the product that will be suggested.
 * @param {Array<string>} props.productSuggestionIds - Products suggested array.
 * @param {SuggestionsColumn} props.ideas - Ideas column content.
 * @param {Array<StorySuggestion>} props.storiesSuggestions - Stories suggested array.
 * @param {React.MouseEventHandler<HTMLAnchorElement>} props.onResultClick - Callback for result click event.
 * @param {Function} [props.onProductClick] - Callback for product click event.
 *
 * @returns {React.FC<SearchResultsProps>} Rendered SearchResults component.
 */
const SearchResults: React.FC<SearchResultsProps> = ({
    searchValue,
    productSuggestionIds,
    searchSuggestion,
    ideas,
    storiesSuggestions,
    onResultClick,
    onProductClick,
}) => {
    const styles = useMultiStyleConfig('SearchResults') as Record<string, GridProps & TextProps & LinkProps & StackProps>
    const {locale} = useMultiSite()
    const {analyticsEventInternalSearch} = useAnalytics()

    const {data: products, isLoading: isProductsLoading} = useProducts({
        parameters: {
            ids: productSuggestionIds.join(','),
            allImages: false,
            expand: ['none'],
            c_productTileHeader: true,
        },
    })

    const searchTerm = searchSuggestion ?? searchValue

    const renderHighlightedText = () => {
        if (searchSuggestion?.startsWith(searchValue)) {
            const remainingText = searchSuggestion.slice(searchValue.length)
            return (
                <Text as="span" {...styles.suggestionText}>
                    <Text as="span" {...styles.searchText}>
                        {searchValue}
                    </Text>
                    <Text as="span">{remainingText}</Text>
                </Text>
            )
        }
        return <>{searchValue}</>
    }

    const handleTipClick = () => {
        analyticsEventInternalSearch({action: 'Tips', label: searchTerm})
    }

    const handleSuggestionClick = () => {
        analyticsEventInternalSearch({action: 'Suggestion', label: searchSuggestion!})
    }

    return (
        <Box data-testid="search-results">
            {searchSuggestion && (
                <Text data-testid="search-suggestion" {...styles.suggestionLabel}>
                    <FormattedMessage id="label.suggest.doyoumean" defaultMessage="Did you mean:" />{' '}
                    <Link
                        data-testid="search-suggestion-link"
                        variant="alwaysUnderline"
                        {...styles.suggestionLink}
                        as={RouteLink}
                        to={searchUrlBuilder(searchSuggestion, locale.alias)}
                        onClick={handleSuggestionClick}
                    >
                        {searchSuggestion}
                    </Link>
                </Text>
            )}

            <ShowBelowViewport breakpoint="lg">
                <Text {...styles.searchedLabel}>
                    <Link href={searchUrlBuilder(searchTerm, locale.alias)} onClick={onResultClick}>
                        {renderHighlightedText()}
                    </Link>
                </Text>
            </ShowBelowViewport>

            <Grid data-testid="search-results-grid" {...styles.mainGrid}>
                <GridItem>
                    <Grid {...styles.leftGrid}>
                        {/* Stories column */}
                        <HideBelowViewport breakpoint="lg" as={GridItem}>
                            <Text as="h2" {...styles.columnTitle}>
                                <FormattedMessage id="label.suggest.inspiration" defaultMessage="Stories" />
                            </Text>
                            <Stack {...styles.storyImages}>
                                {storiesSuggestions?.map((story) => (
                                    <Link href={story.href} key={story.name}>
                                        <AspectRatio ratio={178 / 111}>
                                            <Image src={story.image.url} title={story.name} alt={story.image.alt} />
                                        </AspectRatio>
                                    </Link>
                                ))}
                            </Stack>
                        </HideBelowViewport>

                        {/* Ideas column */}
                        <GridItem>
                            {ideas && (
                                <>
                                    <Text as="h2" {...styles.columnTitle}>
                                        {ideas.title.name}
                                    </Text>
                                    <Stack {...styles.ideasList}>
                                        {ideas.items.map((idea) => (
                                            <MenuItem key={idea.name} href={idea.href} variant="ideaSuggestion">
                                                {idea.name}
                                            </MenuItem>
                                        ))}
                                    </Stack>
                                </>
                            )}
                        </GridItem>

                        {/* Divider column */}
                        <HideBelowViewport breakpoint="lg" as={GridItem}>
                            <Divider orientation="vertical" aria-orientation={undefined} {...styles.gridDivider} />
                        </HideBelowViewport>

                        {/* Results column */}
                        <HideBelowViewport breakpoint="lg" as={GridItem} overflow="hidden">
                            <Text as="h2" {...styles.columnTitle}>
                                <FormattedMessage id="label.suggest.products.results" defaultMessage="Results" />
                            </Text>
                            <Link
                                data-testid="results-link"
                                {...styles.searchedLabel}
                                as={RouteLink}
                                to={searchUrlBuilder(searchTerm, locale.alias)}
                                onClick={handleTipClick}
                            >
                                {renderHighlightedText()}
                            </Link>
                        </HideBelowViewport>
                    </Grid>
                </GridItem>

                {/* Product Images grid column */}
                <GridItem>
                    <SearchProductsGrid products={products?.data} isLoading={isProductsLoading} onProductClick={onProductClick} />
                </GridItem>
            </Grid>
        </Box>
    )
}

export default SearchResults
